/// <reference path="../assets.d.ts" />
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  LocateFixed,
  Maximize2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { getDocument, GlobalWorkerOptions, Util, type PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  IconButton,
} from '@lognai/synapse-design-system-v2';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export interface InvoiceEvidenceCitation {
  id: string;
  label: string;
  kind?: string;
  page?: number;
  sheet?: string;
  cellRange?: string;
  excerpt?: string;
  region?: { x: number; y: number; width: number; height: number };
}

export interface InvoiceEvidenceDocument {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  category?: string;
}

interface InvoiceEvidencePreviewProps {
  document?: InvoiceEvidenceDocument;
  previewUrl?: string;
  citations?: InvoiceEvidenceCitation[];
  selectedCitationId?: string;
  onSelectCitation?: (citation: InvoiceEvidenceCitation) => void;
  onOpenOriginal?: () => void;
  className?: string;
  fitContainer?: boolean;
  /**
   * Show the browsable citation column beside the document.
   *
   * The invoice review screen turns this off: proof there is reached by
   * clicking the field it belongs to, and the column would be a second, rival
   * way to do the same thing. Payment intake still uses it.
   */
  showCitationList?: boolean;
  /**
   * The field the reviewer is on. With the citation list gone, this pane is the
   * only place proof appears, so it has to answer "nothing was cited here" as
   * plainly as it answers "here it is".
   */
  activeFieldLabel?: string;
}

const location = (citation: InvoiceEvidenceCitation) => {
  if (citation.page) return `Page ${citation.page}`;
  if (citation.sheet)
    return `${citation.sheet}${citation.cellRange ? ` · ${citation.cellRange}` : ''}`;
  return citation.kind?.replace(/_/g, ' ') || 'Source evidence';
};

const normalizedRegion = (region?: InvoiceEvidenceCitation['region']) => {
  if (!region) return undefined;
  const values = [region.x, region.y, region.width, region.height];
  if (!values.every(Number.isFinite)) return undefined;
  const maximum = Math.max(...values.map((value) => Math.abs(value)));
  const divisor = maximum > 1 ? (maximum <= 100 ? 100 : 1000) : 1;
  return {
    x: Math.max(0, Math.min(1, region.x / divisor)),
    y: Math.max(0, Math.min(1, region.y / divisor)),
    width: Math.max(0, Math.min(1, region.width / divisor)),
    height: Math.max(0, Math.min(1, region.height / divisor)),
  };
};

function PdfPreview({ url, citation, fitContainer = false }: { url: string; citation?: InvoiceEvidenceCitation; fitContainer?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRenderRef = useRef<{
    cancel: () => void;
    promise: Promise<void>;
  }>();
  const [pdf, setPdf] = useState<PDFDocumentProxy>();
  const citationPage = citation?.page && citation.page > 0 ? citation.page : 1;
  const [page, setPage] = useState(citationPage);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState<string>();
  /**
   * Width available for the page, tracked live.
   *
   * Zoom used to be a fixed multiple of the PDF's own point size, so "100%" was
   * whatever the source page happened to be — on a US Letter page in a column
   * narrower than 816px it overflowed, and the Fit-to-width control did not fit
   * anything. Zoom is now a multiple of the fitted width, so 100% fits by
   * definition at whatever width the column currently has.
   */
  const [available, setAvailable] = useState(0);
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setAvailable(entry.contentRect.width));
    observer.observe(node);
    setAvailable(node.clientWidth);
    return () => observer.disconnect();
  }, [pdf]);
  const [inferredHighlights, setInferredHighlights] = useState<
    Array<{ x: number; y: number; width: number; height: number }>
  >([]);

  useEffect(() => {
    let active = true;
    setPdf(undefined);
    setError(undefined);
    const task = getDocument(url);
    void task.promise
      .then((loaded) => {
        if (active) setPdf(loaded);
        else void loaded.destroy();
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message || 'Could not render this PDF.');
      });
    return () => {
      active = false;
      void task.destroy();
    };
  }, [url]);

  useEffect(() => {
    if (citation) setPage(citation.page && citation.page > 0 ? citation.page : 1);
  }, [citation?.id, citation?.page]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pdf) return;
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<void> } | undefined;
    setInferredHighlights([]);
    setError(undefined);
    const render = async () => {
      const previousRender = activeRenderRef.current;
      if (previousRender) {
        previousRender.cancel();
        try {
          await previousRender.promise;
        } catch {
          // PDF.js rejects a cancelled render. Waiting for that rejection is
          // still required before the canvas may be used by another render.
        }
        if (activeRenderRef.current === previousRender) activeRenderRef.current = undefined;
      }
      if (cancelled) return;

      const pdfPage = await pdf.getPage(Math.min(page, pdf.numPages));
      if (cancelled) return;
      const natural = pdfPage.getViewport({ scale: 1 });
      // `available` is the padded content box; leave the fitted page a hair of
      // room so its shadow is not flush against the scroll edge.
      const fit = available && natural.width ? (available - 8) / natural.width : 0.95;
      const viewport = pdfPage.getViewport({ scale: fit * zoom });
      const density = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(viewport.width * density);
      canvas.height = Math.floor(viewport.height * density);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const context = canvas.getContext('2d');
      if (!context) return;
      const citationOnPage = Boolean(citation) && citationPage === page;
      if (citationOnPage && !normalizedRegion(citation?.region) && citation?.excerpt?.trim()) {
          const normalize = (text: string) => text.toLowerCase().replace(/\s+/g, ' ').trim();
          const target = normalize(citation.excerpt);
          const labelTarget = normalize(citation.label.replace(/[_-]+/g, ' '));
          const text = await pdfPage.getTextContent();
          if (cancelled) return;
          const boxes = text.items.flatMap((item) => {
            if (!('str' in item) || !item.str.trim()) return [];
            const transform = Util.transform(viewport.transform, item.transform);
            const fontHeight = Math.max(8, Math.hypot(transform[2], transform[3]));
            const style = text.styles[item.fontName];
            const ascent = style?.ascent
              ? style.ascent * fontHeight
              : style?.descent
                ? (1 + style.descent) * fontHeight
                : fontHeight * 0.82;
            return [
              {
                text: item.str,
                x: transform[4],
                y: transform[5] - ascent,
                width: Math.max(4, item.width * viewport.scale),
                height: Math.max(fontHeight, item.height * viewport.scale),
                baseline: transform[5],
              },
            ];
          });
          let best: { score: number; boxes: typeof boxes } | undefined;
          const score = (candidate: string, expected: string) => {
            if (!candidate || !expected) return 0;
            if (candidate === expected) return 10_000;
            if (candidate.includes(expected)) return 8_000 + expected.length / candidate.length;
            if (expected.includes(candidate)) return 4_000 + candidate.length / expected.length;
            return 0;
          };
          for (let start = 0; start < boxes.length; start += 1) {
            const first = boxes[start];
            if (!first) continue;
            for (let end = start; end < Math.min(boxes.length, start + 12); end += 1) {
              const last = boxes[end];
              if (!last) continue;
              if (
                Math.abs(last.baseline - first.baseline) >
                Math.max(first.height, last.height) * 0.5
              )
                break;
              const window = boxes.slice(start, end + 1);
              const candidateText = normalize(window.map((item) => item.text).join(' '));
              const candidateScore = Math.max(
                score(candidateText, target),
                score(candidateText, labelTarget) * 0.35
              );
              if (candidateScore > (best?.score ?? 0))
                best = { score: candidateScore, boxes: window };
            }
          }
          if (best?.score) {
            const left = Math.min(...best.boxes.map((item) => item.x));
            const top = Math.min(...best.boxes.map((item) => item.y));
            const right = Math.max(...best.boxes.map((item) => item.x + item.width));
            const bottom = Math.max(...best.boxes.map((item) => item.y + item.height));
            if (!cancelled)
              setInferredHighlights([
                {
                  x: Math.max(0, left - 3),
                  y: Math.max(0, top - 2),
                  width: Math.min(viewport.width - left + 3, right - left + 6),
                  height: Math.max(10, bottom - top + 4),
                },
              ]);
          }
      }
      if (cancelled) return;
      renderTask = pdfPage.render({
        canvasContext: context,
        viewport,
        transform: [density, 0, 0, density, 0, 0],
      });
      activeRenderRef.current = renderTask;
      try {
        await renderTask.promise;
      } finally {
        if (activeRenderRef.current === renderTask) activeRenderRef.current = undefined;
      }
      if (cancelled) return;
      // Keep the canvas dimensions tied to the PDF page even when a viewer loads
      // a very small source page. This also prevents stale render sizes.
      if (!natural.width) canvas.style.width = '100%';
    };
    void render().catch((reason: Error) => {
      if (!cancelled && reason.name !== 'RenderingCancelledException') setError(reason.message);
    });
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [citation, citationPage, pdf, page, zoom, available]);

  if (error) return <ErrorState compact title="Preview unavailable" message={error} />;
  if (!pdf)
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading source document…
      </div>
    );

  return (
    <div
      ref={scrollRef}
      // w-full because this is a flex item: without it the pane sizes to its
      // content and the fitted page would chase its own width downwards.
      className={`flex h-full ${fitContainer ? 'min-h-0' : 'min-h-[420px]'} w-full flex-col overflow-auto bg-muted/20 p-4`}
    >
      <div className="sticky top-0 z-10 mb-3 flex items-center gap-1 self-center rounded-lg border border-border bg-background/95 p-1 shadow-sm backdrop-blur">
        <IconButton
          icon={ChevronLeft}
          aria-label="Previous page"
          title="Previous page"
          disabled={page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
        />
        <span className="min-w-16 text-center text-xs font-medium">
          {page} / {pdf.numPages}
        </span>
        <IconButton
          icon={ChevronRight}
          aria-label="Next page"
          title="Next page"
          disabled={page >= pdf.numPages}
          onClick={() => setPage((current) => Math.min(pdf.numPages, current + 1))}
        />
        <span className="mx-1 h-5 w-px bg-border" />
        <IconButton
          icon={ZoomOut}
          aria-label="Zoom out"
          title="Zoom out"
          disabled={zoom <= 0.75}
          onClick={() => setZoom((current) => Math.max(0.75, current - 0.25))}
        />
        <span className="min-w-12 text-center text-xs tabular-nums">{Math.round(zoom * 100)}%</span>
        <IconButton
          icon={ZoomIn}
          aria-label="Zoom in"
          title="Zoom in"
          disabled={zoom >= 2.5}
          onClick={() => setZoom((current) => Math.min(2.5, current + 0.25))}
        />
        <IconButton
          icon={Maximize2}
          aria-label="Fit to width"
          title="Fit to width"
          disabled={zoom === 1}
          onClick={() => setZoom(1)}
        />
      </div>
      {/*
        Centred only while the page fits. An auto margin on an overflowing child
        pushes its start edge outside the scroll range, so a zoomed-in page has
        to hug the start edge and pan instead — which is what a PDF viewer does.
      */}
      <div className={`relative w-fit shadow-md ${zoom > 1 ? '' : 'mx-auto'}`}>
        <canvas ref={canvasRef} className="block bg-white" />
        {citation && citationPage === page && normalizedRegion(citation.region) && (
          <div
            className="pointer-events-none absolute border-2 border-primary bg-primary/15 shadow-[0_0_0_2px_rgba(255,255,255,0.8)]"
            aria-label={`Extracted field: ${citation.label}`}
            style={{
              left: `${normalizedRegion(citation.region)!.x * 100}%`,
              top: `${normalizedRegion(citation.region)!.y * 100}%`,
              width: `${normalizedRegion(citation.region)!.width * 100}%`,
              height: `${normalizedRegion(citation.region)!.height * 100}%`,
            }}
          />
        )}
        {citation &&
          citationPage === page &&
          inferredHighlights.map((highlight, index) => (
            <div
              key={`${citation.id}-inferred-${index}`}
              className="pointer-events-none absolute rounded-sm border-2 border-primary bg-primary/20 shadow-[0_0_0_2px_rgba(255,255,255,0.75)]"
              style={highlight}
            />
          ))}
        {citation &&
          citationPage === page &&
          !normalizedRegion(citation.region) &&
          !inferredHighlights.length &&
          citation.excerpt && (
            <div className="pointer-events-none absolute inset-x-3 bottom-3 rounded-lg border border-primary bg-background/95 p-3 text-xs shadow-lg">
              <span className="font-semibold text-primary">{citation.label}</span>
              <span className="mt-1 block">“{citation.excerpt}”</span>
            </div>
          )}
      </div>
    </div>
  );
}

export function InvoiceEvidencePreview({
  document,
  previewUrl,
  citations = [],
  selectedCitationId,
  onSelectCitation,
  onOpenOriginal,
  className = '',
  activeFieldLabel,
  showCitationList = true,
  fitContainer = false,
}: InvoiceEvidencePreviewProps) {
  // Without the list, proof is only ever reached through a field, so falling
  // back to citations[0] would present unrelated proof as if it belonged to
  // whatever the reviewer just clicked. With the list, a default selection is
  // what the column is for.
  const activeCitation = useMemo(
    () =>
      citations.find((item) => item.id === selectedCitationId) ??
      (showCitationList ? citations[0] : undefined),
    [citations, selectedCitationId, showCitationList]
  );
  const isPdf = document?.mimeType === 'application/pdf';
  const isImage = document?.mimeType.startsWith('image/');

  if (!document)
    return (
      <Card
        className={`grid min-h-[420px] place-items-center border-dashed p-8 text-center ${className}`}
      >
        <EmptyState
          compact
          icon={<FileText />}
          title="Select a source document"
          description="The original file and extraction evidence will appear here."
        />
      </Card>
    );

  return (
    <Card className={`flex min-h-0 flex-col overflow-hidden ${className}`}>
      <header className="flex min-h-14 items-center justify-between gap-3 border-b border-border bg-surface px-4 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{document.fileName}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge size="xs" tone="neutral">
              {document.category?.replace(/_/g, ' ') ?? 'Source document'}
            </Badge>
            <span>{Math.ceil(document.size / 1024)} KB</span>
            <span>{citations.length} cited fields</span>
          </div>
        </div>
        {previewUrl && onOpenOriginal && (
          <Button size="sm" variant="ghost" onClick={onOpenOriginal}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Open original
          </Button>
        )}
      </header>
      <div
        className={`grid min-h-0 flex-1 ${showCitationList ? 'lg:grid-cols-[minmax(0,1fr)_280px]' : activeFieldLabel ? 'grid-rows-[auto_minmax(0,1fr)]' : 'grid-rows-[minmax(0,1fr)]'}`}
      >
        {/*
          With no citation column, the reviewer needs to be told what the pane
          is currently showing proof for — and, just as importantly, when a
          field has no proof at all rather than proof they have failed to spot.
        */}
        {activeFieldLabel && (
          <div
            role="status"
            className={`shrink-0 border-b px-4 py-2 text-xs ${
              activeCitation
                ? 'border-primary/40 bg-background/95 backdrop-blur'
                : 'border-warning-border bg-warning-subtle'
            }`}
          >
            {activeCitation ? (
              <>
                <span className="font-semibold text-primary">{activeFieldLabel}</span>
                <span className="ml-2 text-muted-foreground">{location(activeCitation)}</span>
                {activeCitation.excerpt && (
                  <span className="mt-0.5 block truncate text-muted-foreground">
                    “{activeCitation.excerpt}”
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="font-semibold">No source evidence for {activeFieldLabel}</span>
                <span className="mt-0.5 block text-muted-foreground">
                  Nothing in the document was cited for this value. Check it against the original.
                </span>
              </>
            )}
          </div>
        )}
        <div className={`relative flex ${fitContainer ? 'min-h-0' : 'min-h-[420px]'} items-center justify-center overflow-hidden bg-surface`}>
          {!previewUrl ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading source document…
            </div>
          ) : isPdf ? (
            <PdfPreview url={previewUrl} citation={activeCitation} fitContainer={fitContainer} />
          ) : isImage ? (
            <div className="relative max-h-full max-w-full">
              <img
                src={previewUrl}
                alt={document.fileName}
                className="max-h-[62vh] max-w-full object-contain"
              />
              {normalizedRegion(activeCitation?.region) && (
                <div
                  className="pointer-events-none absolute border-2 border-primary bg-primary/15"
                  style={{
                    left: `${normalizedRegion(activeCitation?.region)!.x * 100}%`,
                    top: `${normalizedRegion(activeCitation?.region)!.y * 100}%`,
                    width: `${normalizedRegion(activeCitation?.region)!.width * 100}%`,
                    height: `${normalizedRegion(activeCitation?.region)!.height * 100}%`,
                  }}
                />
              )}
            </div>
          ) : (
            <div className="max-w-xl p-8">
              <FileText className="h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">Structured document preview</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Review this file through its extracted text and exact sheet, cell, email, or
                attachment citations.
              </p>
              {activeCitation && (
                <div className="mt-4 rounded-lg border border-primary bg-primary/5 p-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-primary">{activeCitation.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {location(activeCitation)}
                    </span>
                  </div>
                  <p className="mt-2">
                    {activeCitation.excerpt || 'The extraction identified this source location.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        {showCitationList && (
          <aside className="flex max-h-[38vh] min-h-0 flex-col overflow-hidden border-t border-border bg-surface p-3 lg:h-full lg:max-h-none lg:border-l lg:border-t-0">
            <div className="mb-3 flex shrink-0 items-center gap-2 px-1">
              <LocateFixed className="h-4 w-4 text-primary" />
              <div>
                <div className="text-sm font-semibold">Extraction evidence</div>
                <div className="text-xs text-muted-foreground">
                  Select a field to locate its proof
                </div>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {citations.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
                  No citations were produced. Manual verification is required.
                </div>
              ) : (
                <div className="space-y-2">
                  {citations.map((citation) => {
                    const selected = citation.id === activeCitation?.id;
                    return (
                      <button
                        key={citation.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onSelectCitation?.(citation)}
                        className={`w-full rounded-lg border p-3 text-left transition-colors ${selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
                      >
                        <span className="block text-xs font-semibold">{citation.label}</span>
                        <span className="mt-1 block text-xs font-medium text-primary">
                          {location(citation)}
                        </span>
                        {citation.excerpt && (
                          <span className="mt-1.5 block text-xs leading-5 text-muted-foreground">
                            “{citation.excerpt}”
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        )}
      </div>
    </Card>
  );
}
